import PropTypes from 'prop-types';
import React from 'react';

import { Icon } from '~/eds/src/components/Icon/Icon';
import { Text } from '~/eds/src/components/Text/Text';
import { withUsers } from '~/hocs/withUsers';
import { useIntl } from 'react-intl';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import { sortByStringValue } from '~/utils/array';

import { getUserName } from '../../utils/user';
import EcMultipleSelectValue from '../Shared/EcMultipleSelectValue/EcMultipleSelectValue';
import EcMultiSelect from '../Shared/EcMultiSelect/EcMultiSelect';

function DeprecatedUserMultiSelect({
  ariaLabel,
  usersProvided,
  selectedUserIds,
  width = '300px',
  onChange,
  // injected
  users,
}) {
  const intl = useIntl();

  function getId(option) {
    return option.value;
  }

  function getOptions(userIds) {
    const options = [];
    userIds.forEach((userId) => {
      const user = users[userId];
      if (user) {
        options.push({
          label: getUserName(user, intl),
          value: user.id,
        });
      }
    });
    return options;
  }

  const usersList = usersProvided ? usersProvided : users;
  const options = getOptions(Object.keys(usersList)).sort(
    sortByStringValue('label'),
  );
  const selectedOptions = getOptions(selectedUserIds);

  return (
    <FlexLayout flexDirection="column" space={1}>
      <EcMultiSelect
        aria-label={ariaLabel}
        value={selectedOptions}
        noOptionsMessage={intl.formatMessage({
          id: 'DeprecatedUserMultiSelect.noUsersFound',
          defaultMessage: 'No users found',
          description: 'Message when no users are found in search',
        })}
        options={options}
        placeholder={intl.formatMessage({
          id: 'DeprecatedUserMultiSelect.searchPlaceholder',
          defaultMessage: 'Search for users…',
          description: 'Placeholder text for user search input',
        })}
        width={width}
        onChange={(updatedOptions) => {
          const updatedUserIds = updatedOptions.map(getId);
          onChange(updatedUserIds);
        }}
        getOptionLabel={(option) => option.label}
      />
      <FlexLayout alignItems="center" flexWrap="wrap" space={2}>
        <Icon icon="arrow-forward" role="presentation" />
        {selectedOptions.map(({ label, value }) => (
          <EcMultipleSelectValue
            key={value}
            label={label}
            closeHandler={() => {
              const updatedUserIds = selectedOptions
                .filter((user) => user.value !== value)
                .map(getId);
              onChange(updatedUserIds);
            }}
          />
        ))}
        {selectedOptions.length === 0 && (
          <Text color="text.secondary" variant="body-medium">
            {intl.formatMessage({
              id: 'DeprecatedUserMultiSelect.noUsersSelected',
              defaultMessage: 'No users selected',
              description: 'Message when no users are selected',
            })}
          </Text>
        )}
      </FlexLayout>
    </FlexLayout>
  );
}

DeprecatedUserMultiSelect.propTypes = {
  ariaLabel: PropTypes.string,
  usersProvided: PropTypes.object,
  selectedUserIds: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,
  width: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default withUsers(DeprecatedUserMultiSelect);
